import React, { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAlphaFeatureGates, isFeatureGated, AlphaFeatureGates } from '@/hooks/useAlphaFeatureGates';
import { useToast } from '@/hooks/use-toast';

interface AlphaGateGuardProps {
  /** The feature key to check gate status for */
  feature: keyof Omit<AlphaFeatureGates, 'isAdmin'>;
  /** The child component to render if access is allowed */
  children: React.ReactNode;
  /** Optional redirect path (defaults to '/threads') */
  redirectTo?: string;
}

/**
 * AlphaGateGuard protects routes based on alpha feature gate configuration.
 *
 * - If the user is an Admin/System Admin, children are always rendered
 * - If the feature is gated (disabled) for non-admins, user is redirected
 *   to the specified path with a "Coming soon" toast notification
 * - If the feature is enabled, children are rendered normally
 *
 * Usage:
 * ```tsx
 * <Route path="/clients">
 *   <AlphaGateGuard feature="clients">
 *     <ClientsPage />
 *   </AlphaGateGuard>
 * </Route>
 * ```
 */
export const AlphaGateGuard: React.FC<AlphaGateGuardProps> = ({
  feature,
  children,
  redirectTo = '/',
}) => {
  const { gates, isLoading } = useAlphaFeatureGates();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const isGated = isFeatureGated(gates, feature);

  useEffect(() => {
    if (!isLoading && isGated) {
      // Show toast notification
      toast({
        title: 'Coming soon...',
        description: 'This feature will be available in a future update.',
        duration: 4000,
      });
      // Redirect to home
      navigate(redirectTo);
    }
  }, [isGated, isLoading, navigate, redirectTo, toast]);

  // While loading, show nothing (could add a spinner if needed)
  if (isLoading) {
    return null;
  }

  // If gated, don't render children (redirect effect will handle navigation)
  if (isGated) {
    return null;
  }

  // Feature is accessible, render children
  return <>{children}</>;
};

export default AlphaGateGuard;
